import TrashCan16 from "@carbon/icons-react/es/trash-can/16";
import { Col, Modal as AntdModal, Row } from "antd";
import { ColumnProps } from "antd/lib/table/Column";
import { TFunction } from "i18next";
import { AppUser } from "models/AppUser";
import { AppUserGrouping, AppUserGroupingFilter } from "models/AppUserGrouping";
import React, { useMemo } from "react";
import {
  Button,
  LayoutCell,
  LayoutHeader,
  Modal,
  OneLineText,
  Pagination,
  StandardTable,
} from "react3l-ui-library";
import InputSearch from "react3l-ui-library/build/components/Input/InputSearch";
import { projectDocumentUserGroupingRepository } from "repositories/project-document-user-grouping-repository";
import { finalize, forkJoin } from "rxjs";
import { webService } from "services/common-services/web-service";
import nameof from "ts-nameof.macro";
import { Close20 } from "@carbon/icons-react";
import { APP_USER_GROUPING_PROJECT_PROFILE_ROUTE } from "config/route-consts";
import { authorizationService } from "services/common-services/authorization-service";
import { API_PROJECT_DOCUMENT_USER_GROUPING_PREFIX } from "config/api-consts";

export function useAppUserGroupingProjectProfileMappingHook() {
  const [visible, setVisible] = React.useState<boolean>(false);
  const [subscription] = webService.useSubscription();
  const [appUserGroupingFilter, setAppUserGroupingFilter] =
    React.useState<AppUserGroupingFilter>({
      ...new AppUserGroupingFilter(),
      skip: 0,
      take: 10,
    });
  const [list, setList] = React.useState<AppUserGrouping[]>([]);
  const [total, setTotal] = React.useState<number>(undefined);
  const [loading, setLoading] = React.useState<boolean>();
  const [currentAppUser, setCurrentAppUser] = React.useState<AppUser>(null);
  const requestIdRef = React.useRef<number>(0);

  const handleGetItemList = React.useCallback(
    async (filterValue) => {
      const currentRequestId = ++requestIdRef.current;
      setLoading(true);
      const getNCountItems = forkJoin([
        projectDocumentUserGroupingRepository.list(filterValue),
        projectDocumentUserGroupingRepository.count(filterValue),
      ])
        .pipe(
          finalize(() => {
            if (currentRequestId === requestIdRef.current) {
              setLoading(false);
            }
          })
        )
        .subscribe(
          (results: [AppUser[], number]) => {
            if (currentRequestId === requestIdRef.current && results[0]) {
              setList([...results[0]]);
              setTotal(Number(results[1]));
            }
          },
          (errors: any) => {}
        );
      subscription.add(getNCountItems);
    },
    [subscription]
  );

  const handleOpenModal = React.useCallback(
    (id, appUser) => (event: any) => {
      setCurrentAppUser(appUser);
      handleGetItemList({
        ...new AppUserGroupingFilter(),
        appUserId: { equal: id },
        skip: 0,
        take: 10,
      });
      setAppUserGroupingFilter({
        ...new AppUserGroupingFilter(),
        appUserId: { equal: id },
        skip: 0,
        take: 10,
      });

      setVisible(true);
    },
    [handleGetItemList]
  );
  const handleCloseModal = React.useCallback(() => {
    setVisible(false);
  }, []);

  return {
    visible,
    list,
    total,
    appUserGroupingFilter,
    setAppUserGroupingFilter,
    loading,
    handleOpenModal,
    handleCloseModal,
    currentAppUser,
    handleGetItemList,
  };
}
interface AppUserGroupingProjectProfileAppUserMappingModalProps {
  visible: boolean;
  handleClose: () => void;
  translate: TFunction;
  appUserGroupingFilter?: AppUserGroupingFilter;
  listAppUserGrouping?: AppUserGrouping[];
  countAppUserGrouping?: number;
  loadList?: boolean;
  handleTableChange?: (value: any) => void;
  handlePagination?: (skip: number, take: number) => void;
  currentAppUser?: AppUser;
  setAppUserGroupingFilter?: any;
  handleGetItemList?: (filterValue: any) => Promise<void>;
  handleReloadListAppUser?: () => void;
}
export function AppUserGroupingProjectProfileMappingModal(
  props: AppUserGroupingProjectProfileAppUserMappingModalProps
) {
  const { validAction } = authorizationService.useAuthorizedAction(
    APP_USER_GROUPING_PROJECT_PROFILE_ROUTE.split("/")[
      APP_USER_GROUPING_PROJECT_PROFILE_ROUTE.split("/").length - 1
    ],
    API_PROJECT_DOCUMENT_USER_GROUPING_PREFIX
  );
  const {
    visible,
    translate,
    appUserGroupingFilter,
    listAppUserGrouping,
    countAppUserGrouping,
    loadList,
    handleTableChange,
    handlePagination,
    handleClose,
    setAppUserGroupingFilter,
    currentAppUser,
    handleGetItemList,
    handleReloadListAppUser,
  } = props;

  const [loadingDelete, setLoadingDelete] = React.useState<boolean>(false);

  const handleDeleteItem = React.useCallback(
    (id) => {
      setLoadingDelete(true);
      projectDocumentUserGroupingRepository
        .removeAppUser({
          appUserId: currentAppUser?.id,
          projectDocumentUserGroupingId: id,
        })
        .pipe(
          finalize(() => {
            setLoadingDelete(false);
          })
        )
        .subscribe(
          (res) => {
            setAppUserGroupingFilter({ ...appUserGroupingFilter });
            handleGetItemList(appUserGroupingFilter);
            handleReloadListAppUser();
          },
          (err) => {}
        );
    },
    [
      currentAppUser?.id,
      setAppUserGroupingFilter,
      appUserGroupingFilter,
      handleGetItemList,
      handleReloadListAppUser,
    ]
  );
  const onClickDeleteIcon = React.useCallback(
    (id: number) => {
      AntdModal.confirm({
        title: translate("general.deletes.content"),
        content: translate("general.deletes.title"),
        cancelText: translate("general.deletes.cancel"),
        okType: "danger",
        onOk() {
          handleDeleteItem(id);
        },
      });
    },
    [handleDeleteItem, translate]
  );

  const handleFilter = React.useCallback(
    (value: any) => {
      const newFilter = { ...appUserGroupingFilter };
      newFilter["search"] = value;
      newFilter["skip"] = 0;
      setAppUserGroupingFilter({ ...newFilter });
      handleGetItemList(newFilter);
    },
    [appUserGroupingFilter, handleGetItemList, setAppUserGroupingFilter]
  );
  const columns: ColumnProps<AppUserGrouping>[] = useMemo(
    () => [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUserGroupings.code")}
          />
        ),
        key: nameof(listAppUserGrouping[0].code),
        dataIndex: nameof(listAppUserGrouping[0].code),

        ellipsis: true,
        render(...params: [string, AppUserGrouping, number]) {
          return (
            <LayoutCell orderType="left" tableSize="sm">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },

      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("appUserGroupings.name")}
          />
        ),
        key: nameof(listAppUserGrouping[0].name),
        dataIndex: nameof(listAppUserGrouping[0].name),

        ellipsis: true,
        render(...params: [string, AppUserGrouping, number]) {
          return (
            <LayoutCell orderType="left" tableSize="sm">
              <OneLineText value={params[0]} />
            </LayoutCell>
          );
        },
      },

      {
        key: "action",
        dataIndex: nameof(listAppUserGrouping[0].id),
        fixed: "right",
        width: 150,
        align: "center",
        render(id: number, appUserGrouping: AppUserGrouping) {
          return validAction("removeAppUser") ||
            validAction("removeAppUserGrouping") ? (
            <Button
              type="icon-only-ghost"
              icon={<TrashCan16 />}
              className="btn--sm"
              onClick={() => onClickDeleteIcon(id)}
            />
          ) : (
            <></>
          );
        },
      },
    ],
    [onClickDeleteIcon, listAppUserGrouping, translate, validAction]
  );
  return (
    <>
      <Modal
        size={720}
        visibleFooter={false}
        visible={visible}
        title={translate(`${currentAppUser?.name}`)}
        closable={false}
        handleCancel={handleClose}
        wrapClassName={"dxg-modal-wrap"}
      >
        <div className="modal-content">
          <Row>
            <Col lg={24}>
              <InputSearch
                valueFilter={appUserGroupingFilter}
                value={appUserGroupingFilter?.search}
                classFilter={AppUserGroupingFilter}
                placeHolder={translate("appUserGroupings.placeholder.name")}
                onChange={handleFilter}
                className="app-user-grouping__input-search"
                animationInput={false}
              />
            </Col>
          </Row>
          <StandardTable
            rowKey={nameof(listAppUserGrouping[0].id)}
            columns={columns}
            dataSource={listAppUserGrouping}
            isDragable={true}
            tableSize={"md"}
            onChange={handleTableChange}
            loading={loadList || loadingDelete}
          />
          <Pagination
            skip={appUserGroupingFilter?.skip}
            take={appUserGroupingFilter?.take}
            total={countAppUserGrouping}
            onChange={handlePagination}
            canChangePageSize={false}
          />
        </div>

        <span className={"close-modal"} onClick={handleClose}>
          <Close20 />
        </span>
      </Modal>
    </>
  );
}
